import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Children, EditPerson, Person } from '../../interfaces/persons.interface';
import { PersonsService } from '../../services/persons.service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDeleteDialogComponent } from '../../shared/delete-confirm-dialog.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UsersService } from '../../services/users.service';

@Component({
  selector: 'app-family',
  standalone: false,
  templateUrl: './family.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./family.component.scss']
})
export class FamilyComponent implements OnInit {
  @Output() personChanged = new EventEmitter<void>();
  @Output() childChanged = new EventEmitter<void>();
  @Input() parent!: Person; // Parent person object
  children: Children[] = [];
  editingChildId?: number;
  childForm!: FormGroup;
  newChildForm!: FormGroup;
  parentForm!: FormGroup;
  childAccountForms: Record<number, FormGroup> = {};
  childEmailChangeForms: Record<number, FormGroup> = {};
  childEmailEditMode: Record<number, boolean> = {};
  childAccountLoading: Record<number, boolean> = {};
  childAccountCreated: Record<number, boolean> = {};
  loading = false;

  // Date restrictions for birthday field
  maxDate: Date;
  minDate: Date;

  constructor(
    private fb: FormBuilder,
    private personsService: PersonsService,
    private usersService: UsersService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) {
    // Set date restrictions
    this.maxDate = new Date(); // Today
    this.minDate = new Date();
    this.minDate.setFullYear(this.minDate.getFullYear() - 21); // 120 years ago
  }

  ngOnInit(): void {
    this.childForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      surname: ['', [Validators.required, Validators.minLength(2)]],
      birthday: ['', Validators.required],
    });
    this.newChildForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      surname: ['', [Validators.required, Validators.minLength(2)]],
      birthday: ['', Validators.required],
    });
    this.parentForm = this.fb.group({
      confirm_parent_status: [false]
    });
    this.loadChildren();
    this.initializeParentForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['parent'] && changes['parent'].currentValue) {
      this.loadChildren();
      this.initializeParentForm();
    }
  }

  deleteChild(childId: number): void {
    if (!childId) return;
        const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
          width: '320px',
          data: { message: 'Czy na pewno chcesz usunąć to dziecko?' }
        });

        dialogRef.afterClosed().subscribe(result => {
          if (result === true) {
            this.personsService.deletePerson(childId).subscribe({
              next: () => {
                
                this.children = this.children.filter(child => child.id !== childId);
                this.childChanged.emit();
                this.snackBar.open('Osoba została usunięta', 'Zamknij', {
                duration: 8000,
                panelClass: ['success-snackbar'],
                horizontalPosition: 'center', // Center horizontally
                verticalPosition: 'bottom',
              });
              }, error: (err: any) => {
                let errorMessage = 'Wystąpił błąd podczas kasowania osoby!';
                if (err.status === 404) {
                  errorMessage = 'Nie znaleziono osoby do usunięcia.';
                } else if (err.status === 500) {
                  errorMessage = 'Błąd serwera. Spróbuj ponownie później.';
                }

                this.snackBar.open(errorMessage, 'Zamknij', {
                  duration: 20000,
                  panelClass: ['error-snackbar'],
                  horizontalPosition: 'center', // Center horizontally
                  verticalPosition: 'bottom',
                });
              }
            });
          }
        });
  }

  resetChildForm(): void {
    this.childForm.reset();
    this.editingChildId = undefined;
  }

  loadChildren(): void {
    this.children = this.parent?.children || [];
    this.initializeChildAccountForms();
  }

  initializeChildAccountForms(): void {
    const nextForms: Record<number, FormGroup> = {};
    const nextEmailForms: Record<number, FormGroup> = {};
    const nextEmailEditMode: Record<number, boolean> = {};
    const nextLoading: Record<number, boolean> = {};
    const nextCreated: Record<number, boolean> = {};

    for (const child of this.children) {
      if (!child.id) continue;
      nextForms[child.id] = this.childAccountForms[child.id] || this.createChildAccountForm();
      nextEmailForms[child.id] = this.childEmailChangeForms[child.id] || this.createChildEmailChangeForm();
      nextEmailEditMode[child.id] = this.childEmailEditMode[child.id] || false;
      nextLoading[child.id] = this.childAccountLoading[child.id] || false;
      nextCreated[child.id] = this.childAccountCreated[child.id] || (!!child.child_id && child.child_id > 0);
    }

    this.childAccountForms = nextForms;
    this.childEmailChangeForms = nextEmailForms;
    this.childEmailEditMode = nextEmailEditMode;
    this.childAccountLoading = nextLoading;
    this.childAccountCreated = nextCreated;
  }

  hasChildUserAccount(child: Children): boolean {
    if (!child.id) return false;
    return (!!child.child_id && child.child_id > 0) || !!this.childAccountCreated[child.id];
  }

  createChildEmailChangeForm(): FormGroup {
    return this.fb.group({
      email: ['', [Validators.required, Validators.email]],
    });
  }

  createChildAccountForm(): FormGroup {
    return this.fb.group(
      {
        email: ['', [Validators.required, Validators.email]],
        password: ['', [Validators.required, Validators.minLength(8)]],
        rePassword: ['', [Validators.required]],
      },
      { validators: this.passwordsMatchValidator('password', 'rePassword') }
    );
  }

  passwordsMatchValidator(passwordKey: string, confirmPasswordKey: string): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const password = group.get(passwordKey)?.value;
      const confirmPassword = group.get(confirmPasswordKey)?.value;

      if (!password || !confirmPassword) {
        return null;
      }

      return password === confirmPassword ? null : { passwordMismatch: true };
    };
  }

  getChildAccountForm(childId: number): FormGroup {
    if (!this.childAccountForms[childId]) {
      this.childAccountForms[childId] = this.createChildAccountForm();
      this.childAccountLoading[childId] = false;
    }
    return this.childAccountForms[childId];
  }

  getChildEmailChangeForm(childId: number): FormGroup {
    if (!this.childEmailChangeForms[childId]) {
      this.childEmailChangeForms[childId] = this.createChildEmailChangeForm();
      this.childEmailEditMode[childId] = false;
      this.childAccountLoading[childId] = false;
    }
    return this.childEmailChangeForms[childId];
  }

  startChildEmailEdit(child: Children): void {
    if (!child.id) return;

    const form = this.getChildEmailChangeForm(child.id);
    form.patchValue({ email: child.user_email || '' });
    form.markAsPristine();
    this.childEmailEditMode[child.id] = true;
  }

  cancelChildEmailEdit(childId: number): void {
    if (!childId) return;
    this.childEmailEditMode[childId] = false;
  }

  submitChildEmailChange(child: Children): void {
    if (!child.id) return;

    const form = this.getChildEmailChangeForm(child.id);
    if (form.invalid) {
      form.markAllAsTouched();
      return;
    }

    const email = form.get('email')?.value;
    if (!email) return;

    this.childAccountLoading[child.id] = true;
    this.usersService.changeChildUserEmail({ person_id: child.id, email }).subscribe({
      next: () => {
        this.childAccountLoading[child.id!] = false;
        child.user_email = email;
        this.childEmailEditMode[child.id!] = false;

        this.snackBar.open('Email dziecka został zaktualizowany', 'Zamknij', {
          duration: 5000,
          panelClass: ['success-snackbar'],
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
        });
      },
      error: (err: any) => {
        this.childAccountLoading[child.id!] = false;

        let errorMessage = 'Wystąpił błąd podczas zmiany emaila dziecka.';
        if (err.status === 400) {
          errorMessage = 'Nieprawidłowy adres email.';
        } else if (err.status === 404) {
          errorMessage = 'Nie znaleziono konta dziecka.';
        } else if (err.status === 409) {
          errorMessage = 'Podany email jest już zajęty.';
        } else if (err.status === 500) {
          errorMessage = 'Błąd serwera. Spróbuj ponownie później.';
        }

        this.snackBar.open(errorMessage, 'Zamknij', {
          duration: 20000,
          panelClass: ['error-snackbar'],
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
        });
      },
    });
  }

  getChildAge(child: Children): number | null {
    if (!child.birthday) return null;

    // Parse YYYY-MM-DD (or YYYY-MM-DDTHH:mm:ss) as local date to avoid timezone shifts.
    const dateMatch = child.birthday.match(/^(\d{4})-(\d{2})-(\d{2})/);
    const birthDate = dateMatch
      ? new Date(Number(dateMatch[1]), Number(dateMatch[2]) - 1, Number(dateMatch[3]))
      : new Date(child.birthday);
    if (Number.isNaN(birthDate.getTime())) return null;

    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age;
  }

  isChildAccountFormDisabled(child: Children): boolean {

   if (!child.id) return true;
    const age = this.getChildAge(child);
    return age === null || age < 13 || age > 18;
  }

  createChildUserAccount(child: Children): void {
    if (!child.id || this.isChildAccountFormDisabled(child)) {
      return;
    }

    const form = this.getChildAccountForm(child.id);
    if (form.invalid) {
      form.markAllAsTouched();
      return;
    }

    this.childAccountLoading[child.id] = true;

    const { email, password } = form.value;
    const redirectUrl = window.location.origin + '/confirm-email';
    this.usersService.registerByPerson({ person_id: child.id, email, password, redirectUrl }).subscribe({
      next: () => {
        form.reset();
        child.user_email = email;
        this.childAccountCreated[child.id!] = true;
        this.childAccountLoading[child.id!] = false;
        this.snackBar.open('Konto dziecka zostało utworzone poprawnie', 'Zamknij', {
          duration: 5000,
          panelClass: ['success-snackbar'],
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
        });
      },
      error: (err: any) => {
        this.childAccountLoading[child.id!] = false;

        let errorMessage = 'Wystąpił błąd podczas tworzenia konta dziecka.';
        if (err.status === 400) {
          errorMessage = 'Nieprawidłowe dane. Sprawdź formularz.';
        } else if (err.status === 409) {
          errorMessage = 'Konto dla tego dziecka już istnieje.';
        } else if (err.status === 422) {
          errorMessage = 'Dziecko nie spełnia kryteriów wiekowych do utworzenia konta.';
        } else if (err.status === 500) {
          errorMessage = 'Błąd serwera. Spróbuj ponownie później.';
        }

        this.snackBar.open(errorMessage, 'Zamknij', {
          duration: 20000,
          panelClass: ['error-snackbar'],
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
        });
      },
    });
  }

  editChild(child: Children): void {
    this.childForm.patchValue({
      name: child.name,
      surname: child.surname,
      birthday: child.birthday
    });
    // Optionally, store the editing child's id to distinguish between add and edit
    this.editingChildId = child.id;
  }

  updateChild(): void {
    if (this.childForm.invalid || !this.editingChildId || !this.isParentStatusConfirmed) return;

    const child: EditPerson = { ...this.childForm.value };
    child.parent_id = this.parent.id;

    if (child.birthday) {
      // If it's a string, parse as local date
      let date = new Date(child.birthday);

      // Adjust for timezone offset to get local date as YYYY-MM-DD
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      child.birthday = `${year}-${month}-${day}`;
    }

    this.personsService.updatePerson(this.editingChildId, child).subscribe({
      next: () => {
        this.cancelEdit();
        this.personChanged.emit();
        this.loadChildren();

        this.snackBar.open('Dane osoby zostały zapisane poprawnie', 'Zamknij', {
          duration: 9000,
          panelClass: ['success-snackbar'],
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
        });
      },
      error: (err: any) => {
        let errorMessage = 'Wystąpił błąd podczas zapisywania danych!';
        if (err.status === 400) {
          errorMessage = 'Błędne dane. Sprawdź formularz.';
        } else if (err.status === 404) {
          errorMessage = 'Nie znaleziono mieszkańca.';
        } else if (err.status === 500) {
          errorMessage = 'Błąd serwera. Spróbuj ponownie później.';
        }

        this.snackBar.open(errorMessage, 'Zamknij', {
          duration: 20000,
          panelClass: ['error-snackbar'],
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
        });
      },
    });
  }

  cancelEdit(): void {
    this.newChildForm.reset();
    this.editingChildId = undefined;
  }

  addChild(): void {
    if (this.newChildForm.invalid || !this.parent?.id || !this.isParentStatusConfirmed) return;

    const child: EditPerson = { ...this.newChildForm.value };
    child.parent_id = this.parent.id;

    if (child.birthday) {
      // If it's a string, parse as local date
      let date = new Date(child.birthday);

      // Adjust for timezone offset to get local date as YYYY-MM-DD
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      child.birthday = `${year}-${month}-${day}`;
    }

    // Add mode only - confirmation status is already saved via checkbox change
    this.personsService.createPerson(child).subscribe({
      next: () => {
        this.newChildForm.reset();
        this.loadChildren();
        
        this.childChanged.emit();

        this.snackBar.open('Dane osoby zostały zapisane poprawnie', 'Zamknij', {
          duration: 3000,
          panelClass: ['success-snackbar'],
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
        });
      },
      error: (err: any) => {
        let errorMessage = 'Wystąpił błąd podczas zapisywania danych!';


        if (err.status === 409) {
          errorMessage = 'Osoba o takich danych już istnieje. Skontaktuj się z obsługą systemu, jeśli uważasz, że to błąd.';
        } else if (err.status === 400) {
          errorMessage = 'Błędne dane. Sprawdź formularz.';
        } else if (err.status === 500) {
          errorMessage = 'Błąd serwera. Spróbuj ponownie później.';
        }

        this.snackBar.open(errorMessage, 'Zamknij', {
          duration: 20000,
          panelClass: ['error-snackbar'],
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
        });
      }
    });
  }

  initializeParentForm(): void {
    if (this.parent && this.parentForm) {
      this.parentForm.patchValue({
        confirm_parent_status: !!this.parent.confirm_parent_status
      });
    }
  }

  onParentConfirmationChange(): void {
    if (!this.parent?.id) return;

    const updateData: EditPerson = {
      confirm_parent_status: this.parentForm.get('confirm_parent_status')?.value
    };

    this.personsService.updatePerson(this.parent.id, updateData).subscribe({
      next: (updatedParent) => {
        // Update local parent object without emitting events
        if (this.parent) {
          this.parent.confirm_parent_status = updatedParent.confirm_parent_status;
        }
        this.snackBar.open('Status oświadczenia został zapisany', 'Zamknij', {
          duration: 3000,
          panelClass: ['success-snackbar'],
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
        });
      },
      error: (err: any) => {
        let errorMessage = 'Wystąpił błąd podczas zapisywania!';
        if (err.status === 400) {
          errorMessage = 'Błędne dane.';
        } else if (err.status === 500) {
          errorMessage = 'Błąd serwera. Spróbuj ponownie później.';
        }

        this.snackBar.open(errorMessage, 'Zamknij', {
          duration: 20000,
          panelClass: ['error-snackbar'],
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
        });

        // Revert checkbox on error
        this.initializeParentForm();
      }
    });
  }

  get isParentStatusConfirmed(): boolean {
    return !!this.parentForm?.get('confirm_parent_status')?.value;
  }
}